import {Feature} from '../reducers/environmentReducer';
import {type ModalPage} from '../models/IEnvironment';
import {renderCorrectProvinceField, syncCustomerFieldChanges, wcCountryLocaleRuleRender} from './customer';
import {loadScript, $qs, $qsAll} from '../../../@shared/ts/dom';
import {type WCPHPData} from '../../../@type/woocommerce/php-data';
import {type InitMessage} from '../../../@type/window-messages';
import {FeatureFlag} from '../../../@type/features';

declare const google: any;

let shippingAutocomplete: google.maps.places.Autocomplete | undefined;
let newCustomerBillingAutocomplete: google.maps.places.Autocomplete | undefined;

function shouldEnableAddressAutocomplete() {
	if (Feature.enabled(FeatureFlag.ADDRESS_AUTOCOMPLETE)) {
		const activeLocations = Feature.metadata(FeatureFlag.ADDRESS_AUTOCOMPLETE, 'active_locations');

		if (activeLocations === 'default' || activeLocations === 'express_checkout_only') {
			return true;
		}
	}

	return false;
}

export function initAddressAutocomplete(message: InitMessage) {
	if (!shouldEnableAddressAutocomplete()) {
		return;
	}

	loadScript('https://maps.googleapis.com/maps/api/js?key=AIzaSyAL_j0UvKJacQhZ3OedCj_bkcTbuO63MVU&libraries=places', 'google', () => {
		const shippingInput = $qs<HTMLInputElement>('#pp-different-shipping-details [name="shipping_address_1"]');
		const newBillingInput = $qs<HTMLInputElement>('#pp-info-form [name="billing_address_1"]');
		const shippingOptions = {
			/**
			 * Currently using both address_components and formatted_address to cover the most street addresses with least conditional logic
			 * Including more fields costs more, so if enough country specific logic is written to allow the removal of formatted_address, it should be removed
			 */
			fields: ['address_components', 'formatted_address'],
			types: ['address'],
		};
		const billingOptions = {
			/**
			 * Currently using both address_components and formatted_address to cover the most street addresses with least conditional logic
			 * Including more fields costs more, so if enough country specific logic is written to allow the removal of formatted_address, it should be removed
			 */
			fields: ['address_components', 'formatted_address'],
			types: ['address'],
		};

		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
		shippingAutocomplete = new google.maps.places.Autocomplete(shippingInput, shippingOptions);
		shippingAutocomplete?.addListener('place_changed', () => {
			fillInAddress('shipping', 'shipping', message.phpData);
		});
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
		newCustomerBillingAutocomplete = new google.maps.places.Autocomplete(newBillingInput, billingOptions);
		newCustomerBillingAutocomplete?.addListener('place_changed', () => {
			fillInAddress('billing', 'info', message.phpData);
		});

		// Prevent submitting the form when ENTER key is pressed on the input.
		$qsAll('#pp-info-form, #pp-different-shipping-details', $form => {
			$form.addEventListener('keypress', preventSubmitListener);
		});
	}).catch(() => {/**/});
}

function preventSubmitListener(event: Event) {
	const key = (event as KeyboardEvent).key || 0;
	if (key === 'Enter') {
		event.preventDefault();
	}
}

/**
 * Fills in address fields as best possible
 * Implemented mostly to handle US and common European countries. Will need to be tweaked for new countries as merchants ask.
 * Known issues (not resolved due to extreme scope)
 * - For many countries, state/province does not correctly fill due to either inconsistent shortcodes between Google & WooCommerce or different address component name (other than administrative_area_1)
 */
// eslint-disable-next-line complexity
function fillInAddress(section: string, modalPage: string, phpData: WCPHPData) {
	const formIdentifier = modalPage === 'info' ? '#pp-info-form' : '#pp-different-shipping-details';

	const address1Field = $qs<HTMLInputElement>(`${formIdentifier} [name="${section}_address_1"]:not(.hide)`);
	const address2Field = $qs<HTMLInputElement>(`${formIdentifier} [name="${section}_address_2"]:not(.hide)`);
	const postalField = $qs<HTMLInputElement>(`${formIdentifier} [name="${section}_postcode"]:not(.hide)`);
	const cityField = $qs<HTMLInputElement>(`${formIdentifier} [name="${section}_city"]:not(.hide)`);
	const stateField = $qs<HTMLInputElement | HTMLSelectElement>(`${formIdentifier} [name="${section}_state"]:not(.hide)`);
	const provinceField = $qs<HTMLInputElement | HTMLSelectElement>(`${formIdentifier} [name="${section}_province"]:not(.hide)`);

	// Clear all fields, to make it clear to the user what wasn't updated
	if (address1Field) {
		address1Field.value = '';
	}

	if (address2Field) {
		address2Field.value = '';
	}

	if (postalField) {
		postalField.value = '';
	}

	if (cityField) {
		cityField.value = '';
	}

	if (stateField) {
		stateField.value = '';
	}

	if (provinceField) {
		provinceField.value = '';
	}

	let place: google.maps.places.PlaceResult | undefined;
	// Get the place details from the autocomplete object.
	if (modalPage === 'info') {
		place = newCustomerBillingAutocomplete?.getPlace();
	}

	if (modalPage === 'shipping') {
		place = shippingAutocomplete?.getPlace();
	}

	if (!place) {
		return;
	}

	// Set country field first so province/state field can update accordingly.
	const country = place.address_components?.find((component: google.maps.GeocoderAddressComponent) => component.types[0] === 'country');
	const countryField = document.querySelector<HTMLSelectElement>(`${formIdentifier} [name="${section}_country"]`);
	if (countryField && country) {
		const previousCountryFieldValue = countryField.value;
		countryField.value = country.short_name;
		if (previousCountryFieldValue !== country.short_name) {
			renderCorrectProvinceField(phpData, modalPage as ModalPage);
			if (Feature.enabled(FeatureFlag.USE_WC_COUNTRY_LOCALE)) {
				setTimeout(() => {
					wcCountryLocaleRuleRender(modalPage);
				});
			}
		}

		setTimeout(() => {
			syncCustomerFieldChanges(formIdentifier);
		}, 200);
	}

	let postcode = '';

	if (!place.address_components) {
		return;
	}

	// Get each component of the address from the place details,
	// and then fill-in the corresponding field on the form.
	// place.address_components are google.maps.GeocoderAddressComponent objects
	// which are documented at http://goo.gle/3l5i5Mr
	for (const component of place.address_components) {
		const componentType = component.types[0];

		switch (componentType) {
			case 'postal_code': {
				postcode = `${component.long_name}${postcode}`;
				break;
			}

			case 'locality':
			case 'postal_town': {
				if (cityField) {
					cityField.value = component.long_name;
				}

				break;
			}

			// Only autofill state/province value if it isn't hidden by wc rule and if the autofill data is valid.

			case 'administrative_area_level_1': {
				const allowedStatesOrProvinces = phpData.wc_location_info?.allowed_states_or_provinces[country?.short_name ?? ''] ?? {};
				const isInputField = [stateField?.tagName, provinceField?.tagName].includes('INPUT');
				// Break if failed to get allowed states/provinces info from short_name
				if (!Object.keys(allowedStatesOrProvinces).length) {
					break;
				}

				// Break if field type is input and long_name is invalid
				if (isInputField && allowedStatesOrProvinces[component.short_name] !== component.long_name) {
					break;
				}

				const stateValue = isInputField
					? component.long_name
					: Object.keys(allowedStatesOrProvinces).includes(component.short_name)
						? component.short_name
						: findStateKeyByValue(component.long_name, allowedStatesOrProvinces);
				if (stateValue === undefined) {
					break;
				}

				// Fill in both fields, since both will likely be found by the querySelector
				if (stateField) {
					stateField.value = stateValue;
				}

				if (provinceField) {
					provinceField.value = stateValue;
				}

				break;
			}

			case 'country':
				// Should have been set before for loop
				break;
			default:
		}
	}

	if (address1Field && place.formatted_address) {
		address1Field.value = getFormattedStreetAddress(place.formatted_address);
	}

	if (postalField) {
		postalField.value = postcode;
	}

	setTimeout(() => {
		syncCustomerFieldChanges(formIdentifier);
	}, 200);

	// After filling the form with address components from the Autocomplete
	// prediction, set cursor focus on the second address line to encourage
	// entry of subpremise information such as apartment, unit, or floor number.
	if (address2Field) {
		address2Field.focus();
	}
}

/**
 * Should return the properly formatted address for a majority of countries
 * @param formattedAddress Complete formatted address string
 * @returns Formatted street address string
 */
export function getFormattedStreetAddress(formattedAddress: string): string {
	const addressChunks = formattedAddress.split(', ');

	// Edge-case for UAE, as they use hyphens to separate. Will require better logic if other non-comma cases are found.
	if (addressChunks.length === 1) {
		return formattedAddress.split(' - ')[0] ?? '';
	}

	// Regex ensures there's a digit in the string.
	// Originally excluded cases like '2nd', but returned to just digits due to cases like street number '17A' (Australia)
	const regexContainsHouseNumber = /\d+/g;

	// In observed cases, if street number isn't in the first section, it might be in the next section
	if (addressChunks[0] && addressChunks[1] && !regexContainsHouseNumber.test(addressChunks[0]) && regexContainsHouseNumber.test(addressChunks[1])) {
		return `${addressChunks[0]}, ${addressChunks[1]}`;
	}

	// Otherwise, first section is most likely correct
	return addressChunks[0] ?? '';
}

function findStateKeyByValue(value: string, allowedStatesOrProvinces: any) {
	return Object.keys(allowedStatesOrProvinces).find(key => allowedStatesOrProvinces[key] === value);
}
